import { getUrlWithVariant } from "@shopify/theme-product-form";
import { emit } from "@/neon/events";
import { qs, qsa, add, remove } from "@fluorescent/dom";

import ProductForm from "@/lib/product/productForm";
import { dispatchCustomEvent, CustomEvents } from "@/lib/dispatch-custom-event";

import { formatMoney, getProduct } from "@/neon";
import { addItem } from "@/neon/cart";
import { updateRecentProducts } from "@/lib/recentlyViewed";
import {
  updateBuyButton,
  updateUnitPrices,
  inventoryCounter,
  updateSku,
} from "@/lib/product";
import selectors from "@/lib/selectors";
import storeAvailability from "@/lib/storeAvailability";
import ShippingEstimator from "@/lib/shipping-estimator";

const {
  strings: { products: strings },
} = theme;

const classes = {
  hide: "hide",
};

const productForm = (formElement, config = {}) => {
  const { productHandle, ajaxEnabled } = formElement.dataset;
  const { product: selector } = selectors;
  const storeAvailabilityContainer = formElement.querySelector(
    selector.storeAvailability
  );

  const shippingEstimatorButtons = qsa(
    "[data-estimator-trigger]",
    config.container
  );

  const shippingEstimator = shippingEstimatorButtons.map(button =>
    ShippingEstimator(button.parentNode, config.container)
  );

  let thisInventoryCounter;

  const formSubmit = evt => {
    const button = formElement.querySelector(selector.addButton);
    button.classList.add("bttn--loading");

    if (ajaxEnabled !== "true") return;
    evt.preventDefault();

    const quantityError = formElement.querySelector("[data-quantity-error]");
    if (quantityError) {
      quantityError.classList.add("hidden");
    }

    addItem(formElement)
      .then(() => {
        button.classList.remove("bttn--loading");
        emit("product:added");
      })
      .catch(error => {
        if (typeof error.message === "object") {
          const sectionID = qs(selector.giftCardRecipientForm, config.container)
            .dataset.sectionId;

          const errorContainer = qs(
            selector.giftCardRecipientErrors,
            config.container
          );

          const errorList = qs("ul", errorContainer);

          Object.entries(error.message).forEach(([key, value]) => {
            const errorID = `#gift-card-recipient-${key}--${sectionID}`;
            const errorItem = document.createElement("li");
            const errorLink = document.createElement("a");
            const errorInput = qs(errorID, config.container);

            errorLink.href = errorID;
            errorLink.innerText = value;
            errorItem.appendChild(errorLink);
            errorList.appendChild(errorItem);
            add(errorInput, "input-error");
          });

          errorContainer.hidden = false;
        } else {
          if (quantityError) {
            quantityError.classList.remove("hidden");
          }
        }

        button.classList.remove("bttn--loading");
      });
  };

  const defaultConfig = {
    isProductPage: false,
    isFeaturedProduct: false,
    onOptionChange: e => optionChange(e),
    onQuantityChange: e => quantityChange(e),
    onFormSubmit: formSubmit,
    container: formElement,
  };

  const {
    isProductPage,
    isFeaturedProduct,
    onOptionChange,
    onQuantityChange,
    onFormSubmit,
    container,
  } = Object.assign(defaultConfig, config);

  let form = null;
  let availability = null;

  const product = getProduct(productHandle);

  product(data => {
    form = ProductForm(formElement, data, {
      variantSelector: "[data-variant-select]",
      onOptionChange,
      onQuantityChange,
      onFormSubmit,
    });

    if (isProductPage) {
      updateRecentProducts(data);
    }

    // Surface pickup
    const variant = form.getVariant();

    if (storeAvailabilityContainer && variant) {
      availability = storeAvailability(
        storeAvailabilityContainer,
        data,
        variant
      );
    }

    const productInventoryJson = formElement.querySelector(
      "[data-product-inventory-json]"
    );

    if (productInventoryJson) {
      const jsonData = JSON.parse(productInventoryJson.innerHTML);
      const variantsInventories = jsonData.inventory;

      if (variantsInventories) {
        const config = {
          id: variant.id,
          variantsInventories,
        };
        thisInventoryCounter = inventoryCounter(formElement, config);
      }
    }
  });

  // When the user changes the quanitity
  function quantityChange({ dataset: { variant, quantity } }) {
    dispatchCustomEvent(CustomEvents.productQuanityUpdate, {
      quantity: quantity,
      variant: variant,
    });
  }

  // When the user changes a product option
  const optionChange = ({ dataset: { variant }, target }) => {
    // Update product option visable value. Essential for swatch/chips
    const optionSelectedLabel = qs(
      "[data-option-selected]",
      target.closest(".product-form__option")
    );
    if (optionSelectedLabel) optionSelectedLabel.innerHTML = target.value;

    dispatchCustomEvent(CustomEvents.productVariantChange, {
      variant: variant,
    });

    if (thisInventoryCounter) {
      thisInventoryCounter && thisInventoryCounter.update(variant);
    }

    if (!variant) {
      updateBuyButton(formElement);
      updatePrices(variant);

      if (availability && storeAvailabilityContainer) {
        availability.unload();
      }

      shippingEstimatorButtons.forEach(btn => add(btn.parentNode, "hidden"));

      return;
    }

    if (isProductPage) {
      // Update URL with selected variant
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({ path: url }, "", url);
    }

    // Update prices to reflect selected variant
    updatePrices(variant);

    // We need to set the id input manually so the Dynamic Checkout Button works
    const selectedVariantOpt = formElement.querySelector(
      `${selector.variantSelect} ${selector.optionById(variant.id)}`
    );
    selectedVariantOpt.selected = true;

    // We need to dispatch an event so Shopify pay knows the form has changed
    formElement.dispatchEvent(new Event("change"));

    // Update buy button to reflect selected variant
    updateBuyButton(formElement, variant);

    // Update unit pricing
    updateUnitPrices(container, variant);

    // Update sku
    updateSku(container, variant);

    // Scroll to variant media
    if (isProductPage && variant.featured_media) {
      if (window.matchMedia("(min-width: 60em)").matches) {
        const image = document.querySelector(
          `[data-media-id="${variant.featured_media.id}"]`
        );

        if (document.querySelector(".product-thumbnails")) {
          const thumb = document.querySelector(
            `[data-thumbnail-id="${variant.featured_media.id}"]`
          );

          const allThumbs = document.querySelectorAll(
            ".product-thumbnails__item-link"
          );
          const allImages = document.querySelectorAll(".media-wrapper");

          allThumbs.forEach(thumb => thumb.classList.remove("active"));
          allImages.forEach(image => image.classList.add("hidden"));

          image && image.classList.remove("hidden");
          thumb && thumb.classList.add("active");
        } else {
          image && image.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        emit("product:mediaSelect", () => ({
          selectedMedia: variant.featured_media.position - 1,
        }));
      }
    }

    // Show variant
    if (isFeaturedProduct && variant.featured_media) {
      const featureProductImages =
        container.querySelectorAll("[data-media-id]");

      featureProductImages.forEach(element => {
        element.classList.add("hidden");
      });

      const image = container.querySelector(
        `[data-media-id="${variant.featured_media.id}"]`
      );

      image.classList.remove("hidden");
    }

    // Update store availability
    if (
      (availability && isFeaturedProduct) ||
      (availability && isProductPage)
    ) {
      availability.update(variant);
    }

    shippingEstimatorButtons.forEach(btn => remove(btn.parentNode, "hidden"));
  };

  const updatePrices = variant => {
    let target = container ? container : formElement;

    const price = target.querySelectorAll(selector.price);
    const comparePrice = target.querySelectorAll(selector.comparePrice);

    const unavailableString = strings.product.unavailable;

    if (!variant) {
      price.forEach(el => (el.innerHTML = unavailableString));
      comparePrice.forEach(el => (el.innerHTML = ""));
      comparePrice.forEach(el => el.classList.add(classes.hide));
      return;
    }

    price.forEach(el => (el.innerHTML = formatMoney(variant.price)));

    if (variant.compare_at_price > variant.price) {
      comparePrice.forEach(
        el => (el.innerHTML = formatMoney(variant.compare_at_price))
      );
      comparePrice.forEach(el => el.classList.remove(classes.hide));
    } else {
      comparePrice.forEach(el => (el.innerHTML = ""));
      comparePrice.forEach(el => el.classList.add(classes.hide));
    }
  };

  const unload = () => {
    form.destroy();
    shippingEstimator.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
};

export default productForm;
